import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft,
  lucideRefreshCw,
  lucideExternalLink,
  lucideBell,
  lucideStore,
  lucideCheck,
  lucideClipboard,
  lucideCodeXml,
  lucideTriangleAlert,
  lucideGlobe,
  lucideLink,
  lucideSend,
  lucideServer,
  lucideX,
} from '@ng-icons/lucide';

import { UI, type UiBadgeVariant } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { goBackOrNavigate } from '../../utils/navigation';
import { WebhookService, type WebhookEvent } from '../../services/webhook.service';

/**
 * Webhook Hub Detail Component.
 *
 * Full-featured detail page for a single WebhookEvent showing:
 * - Status, event type, provider info
 * - Links to related entities (merchant, application, transaction, entity, provider)
 * - Request payload and headers (collapsible JSON)
 * - Forwarding response details
 * - Confirmation response
 * - Retry action for failed events
 */
@Component({
  selector: 'app-webhook-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, NgIcon, ...UI],
  templateUrl: './webhook-detail.component.html',
  viewProviders: [
    provideIcons({
      lucideArrowLeft,
      lucideRefreshCw,
      lucideExternalLink,
      lucideBell,
      lucideStore,
      lucideCheck,
      lucideClipboard,
      lucideCodeXml,
      lucideTriangleAlert,
      lucideGlobe,
      lucideLink,
      lucideSend,
      lucideServer,
      lucideX,
    }),
  ],
})
export class WebhookDetailComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;
  @Input() id!: string;

  private readonly svc = inject(WebhookService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  readonly shellRoutes = inject(ShellRoutesService);

  /* ─── Data signals ─── */
  readonly event = signal<WebhookEvent | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly retrying = signal(false);
  readonly resending = signal(false);

  /* ─── Copy toast ─── */
  readonly copyToast = signal(false);
  private copyToastTimeout: ReturnType<typeof setTimeout> | null = null;

  /* ─── Collapse state ─── */
  readonly showPayload = signal(true);
  readonly showHeaders = signal(false);
  readonly showForwardResponse = signal(false);
  readonly showForwardRequest = signal(false);
  readonly showForwardRequestHeaders = signal(false);
  readonly showConfirmation = signal(false);

  ngOnInit(): void {
    this.load();
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityConfig.key));
  }

  // ─── Data Loading ──────────────────────────────────────────

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.svc.getWebhookEvent(this.id).subscribe({
      next: (data) => {
        this.event.set(data);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.detail ?? err?.message ?? 'Failed to load webhook event.');
        this.loading.set(false);
      },
    });
  }

  retry(): void {
    this.retrying.set(true);
    this.svc.retryWebhook(this.id).subscribe({
      next: (res) => {
        this.event.set(res.webhook_event);
        this.retrying.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.error ?? err?.message ?? 'Retry failed.');
        this.retrying.set(false);
      },
    });
  }

  resend(): void {
    this.resending.set(true);
    this.error.set(null);
    this.svc.resendWebhook(this.id).subscribe({
      next: (res) => {
        this.event.set(res.webhook_event);
        this.resending.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.error ?? err?.message ?? 'Resend failed.');
        this.resending.set(false);
      },
    });
  }

  // ─── Helpers ───────────────────────────────────────────────

  copyToClipboard(value: string): void {
    navigator.clipboard.writeText(value).then(() => {
      if (this.copyToastTimeout) clearTimeout(this.copyToastTimeout);
      this.copyToast.set(true);
      this.copyToastTimeout = setTimeout(() => this.copyToast.set(false), 1500);
    });
  }

  getStatusVariant(status: string): UiBadgeVariant {
    const map: Record<string, UiBadgeVariant> = {
      PENDING: 'warning',
      PROCESSING: 'info',
      FORWARDED: 'success',
      FAILED: 'danger',
      RETRYING: 'warning',
    };
    return map[status] ?? 'neutral';
  }

  getEventTypeVariant(type: string): UiBadgeVariant {
    const map: Record<string, UiBadgeVariant> = {
      DEPOSIT: 'success',
      PAYOUT: 'info',
      REMITTANCE: 'brand',
      REFUND: 'danger',
      STATUS_UPDATE: 'neutral',
      DIRECT_DEBIT: 'success',
      DEPOSIT_PROFILE: 'success',
      SETTLEMENT: 'brand',
      TRANSACTION_STATUS_CHECK: 'neutral',
      CUSTOMER_LOOKUP: 'neutral',
      ACCOUNT_VALIDATION: 'neutral',
      BALANCE_CHECK: 'neutral',
      SMS_SENDING: 'success',
    };
    return map[type] ?? 'neutral';
  }

  canRetry(): boolean {
    const ev = this.event();
    if (!ev) return false;
    return ev.status === 'FAILED' || ev.status === 'RETRYING';
  }

  canResend(): boolean {
    const ev = this.event();
    if (!ev) return false;
    return ev.status === 'FAILED' || ev.status === 'RETRYING';
  }

  getTransactionRouteKey(eventType: string): string {
    const map: Record<string, string> = {
      DEPOSIT: 'deposits',
      PAYOUT: 'payouts',
      REFUND: 'refunds',
      REMITTANCE: 'remittances',
      TRANSACTION_STATUS_CHECK: 'transaction-status-checks',
      DIRECT_DEBIT: 'direct-debits',
      SETTLEMENT: 'settlements',
      CUSTOMER_LOOKUP: 'customer-lookups',
      ACCOUNT_VALIDATION: 'account-validations',
      BALANCE_CHECK: 'balance-checks',
      SMS_SENDING: 'sms-sendings',
    };
    return map[eventType] ?? 'deposits';
  }

  formatJson(obj: unknown): string {
    if (!obj) return '—';
    if (typeof obj === 'string') {
      try {
        return JSON.stringify(JSON.parse(obj), null, 2);
      } catch {
        return obj;
      }
    }
    return JSON.stringify(obj, null, 2);
  }

  formatDate(value: string | null): string {
    if (!value) return '—';
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return (
        d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) +
        ' ' +
        d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      );
    } catch {
      return value;
    }
  }

  shortId(id: string | null): string {
    if (!id) return '—';
    return id.length > 12 ? id.substring(0, 8) + '...' : id;
  }

  toggleSection(
    section: 'payload' | 'headers' | 'forwardResponse' | 'forwardRequest' | 'forwardRequestHeaders' | 'confirmation'
  ): void {
    switch (section) {
      case 'payload':
        this.showPayload.update((v) => !v);
        break;
      case 'headers':
        this.showHeaders.update((v) => !v);
        break;
      case 'forwardResponse':
        this.showForwardResponse.update((v) => !v);
        break;
      case 'forwardRequest':
        this.showForwardRequest.update((v) => !v);
        break;
      case 'forwardRequestHeaders':
        this.showForwardRequestHeaders.update((v) => !v);
        break;
      case 'confirmation':
        this.showConfirmation.update((v) => !v);
        break;
    }
  }
}
